import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { sanitizeJobContent } from '@/lib/jobContentSanitizer';

interface JobDetailV2BodyProps {
  /** Raw scraped job_full_text from the jobs table. */
  rawContent?: string;
  /** Pre-formatted HTML from formatted_job_content if already populated. */
  formattedContent?: string;
  /** Real job id, used to call format-job-content on demand. */
  jobId?: string;
}

/**
 * Renders the job description body. The fallback chain mirrors the existing
 * JobDetailContent component:
 *
 *   1. If formattedContent is available, render it directly.
 *   2. Otherwise call the format-job-content edge function with the raw text.
 *   3. If that fails or there's no jobId, sanitize the raw text and render.
 *
 * The output goes inside .job-content__rich so the prototype's bullet/heading
 * styling applies regardless of which branch produced the HTML.
 */
export function JobDetailV2Body({
  rawContent,
  formattedContent,
  jobId,
}: JobDetailV2BodyProps) {
  const [html, setHtml] = useState<string | null>(null);
  const [isFormatting, setIsFormatting] = useState(false);

  useEffect(() => {
    if (formattedContent) {
      setHtml(formattedContent);
      return;
    }
    if (!rawContent) {
      setHtml(null);
      return;
    }

    if (jobId && jobId !== 'preview-demo') {
      let cancelled = false;
      setIsFormatting(true);
      (async () => {
        try {
          const { data, error } = await supabase.functions.invoke(
            'format-job-content',
            { body: { job_id: jobId, job_content: rawContent } }
          );
          if (cancelled) return;
          if (error) {
            console.error('format-job-content error:', error);
            setHtml(sanitizeJobContent(rawContent));
          } else if (data?.formatted_content) {
            setHtml(data.formatted_content);
          } else {
            setHtml(sanitizeJobContent(rawContent));
          }
        } catch (err) {
          if (cancelled) return;
          console.error('format-job-content request failed:', err);
          setHtml(sanitizeJobContent(rawContent));
        } finally {
          if (!cancelled) setIsFormatting(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    // No jobId, just sanitize and render.
    setHtml(sanitizeJobContent(rawContent));
  }, [rawContent, formattedContent, jobId]);

  if (!rawContent && !formattedContent) {
    return (
      <p style={{ color: 'var(--af-ink-600)', fontSize: 15 }}>
        No job description available. Hit Apply to view the full posting on the
        company's site.
      </p>
    );
  }

  if (isFormatting) {
    return (
      <div>
        <div className="job-content__loading">
          <span className="job-content__spinner" aria-hidden="true" />
          <span>Pulling the job description into shape.</span>
        </div>
        <div className="job-content__skeleton-row" />
        <div className="job-content__skeleton-row" />
        <div className="job-content__skeleton-row" />
        <div className="job-content__skeleton-row" />
      </div>
    );
  }

  return (
    <div
      className="job-content__rich"
      dangerouslySetInnerHTML={{ __html: html ?? '' }}
    />
  );
}
